// /admin/marketing — Marketing Command Center
//
// Two surfaces:
//   • Daily-draft queue: today's draft, history, manual generation, edit,
//     approve, delete. Always available — runs on Claude Sonnet 4.5 via
//     the Emergent Universal Key.
//   • TikTok connection panel: status badge + Connect/Disconnect + setup
//     instructions. The actual posting requires TIKTOK_CLIENT_KEY +
//     audit approval (2-6 weeks per the TikTok playbook).

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  Film,
  Loader2,
  Plus,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  try { return sessionStorage.getItem("callgod_admin_token") || ""; } catch { return ""; }
}
function setToken(t) { try { sessionStorage.setItem("callgod_admin_token", t); } catch { /* ignore */ } }

async function apiFetch(token, path, options = {}) {
  return fetch(`${API}${path}`, {
    ...options,
    headers: { "X-Admin-Token": token, "Content-Type": "application/json", ...(options.headers || {}) },
  });
}

// ── Token gate ───────────────────────────────────────────────────────
function TokenGate({ onAuthed }) {
  const [t, setT] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const res = await apiFetch(t, "/admin/marketing/themes");
    if (!res.ok) { setErr("Invalid admin token. Check ADMIN_TOKEN in backend/.env."); return; }
    setToken(t);
    onAuthed(t);
  };
  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <form onSubmit={submit} className="cg-card w-full max-w-md rounded-2xl p-7">
        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
        <h2 className="font-serif-display mt-2 text-2xl text-foreground">Marketing Command Center</h2>
        <p className="mt-2 text-[13px] text-muted-foreground">
          Paste your <code className="rounded bg-white/60 px-1">ADMIN_TOKEN</code> to access drafts + TikTok.
        </p>
        <input
          type="password" value={t} onChange={(e) => setT(e.target.value)}
          placeholder="cg_admin_..."
          data-testid="mkt-token-input"
          className="mt-4 w-full rounded-lg border bg-white/70 px-3 py-2 text-[14px]"
          style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}
        />
        {err ? <p className="mt-2 text-[12px] text-red-700">{err}</p> : null}
        <button type="submit" data-testid="mkt-token-submit" className="cg-btn-primary mt-4 w-full !min-h-[40px]">
          Enter
        </button>
      </form>
    </main>
  );
}

// ── TikTok connection panel ──────────────────────────────────────────
function TikTokPanel({ token, status, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!status) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />;

  // Pull the redirect URI from step_2 (the backend now interpolates the real URL).
  const redirectUri = (() => {
    const s = status.instructions?.step_2 || "";
    const m = s.match(/https?:\/\/\S+/);
    return m ? m[0] : "";
  })();

  const copyUri = () => {
    if (!redirectUri) return;
    navigator.clipboard.writeText(redirectUri);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect TikTok? You'll need to re-authorize the brand account to resume autonomous posting.")) return;
    setBusy(true);
    try { await apiFetch(token, "/admin/marketing/tiktok/disconnect", { method: "POST" }); onRefresh(); }
    finally { setBusy(false); }
  };

  return (
    <div className="cg-card rounded-2xl p-5" data-testid="mkt-tiktok-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">TikTok</p>
          <h3 className="font-serif-display text-xl text-foreground">Autonomous posting</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium"
            style={
              status.connected ? { background: "hsla(110,40%,88%,0.85)", color: "hsl(120 30% 25%)" }
              : status.configured ? { background: "hsla(42,60%,88%,0.9)", color: "hsl(30 30% 25%)" }
              : { background: "hsla(20,40%,90%,0.85)", color: "hsl(20 30% 30%)" }
            }
            data-testid="mkt-tiktok-status-badge"
          >
            {status.connected ? <><Check className="h-3 w-3" /> Connected</> :
              status.configured ? "Configured — connect the brand account" :
              "Not configured"}
          </span>
        </div>
      </div>

      {status.connected ? (
        <div className="mt-4 space-y-1 text-[13px] text-foreground/85">
          {status.account?.open_id ? <p>open_id: <span className="font-mono text-[12px] text-muted-foreground">{status.account.open_id}</span></p> : null}
          {status.account?.scope ? <p>scopes: <span className="font-mono text-[12px] text-muted-foreground">{status.account.scope}</span></p> : null}
          {status.account?.access_token_expires_at ? <p className="text-[12px] text-muted-foreground">access token expires {new Date(status.account.access_token_expires_at).toLocaleString()}</p> : null}
          <div className="mt-3">
            <button type="button" disabled={busy} onClick={disconnect} className="cg-btn-ghost !min-h-[34px] !px-3 text-[12px]" data-testid="mkt-tiktok-disconnect">
              Disconnect
            </button>
          </div>
        </div>
      ) : status.configured ? (
        <div className="mt-4">
          <p className="text-[13px] text-foreground/85">
            Your TikTok app keys are set. One last click — log in as the brand account and approve scopes.
          </p>
          <a
            href={`${API}/marketing/tiktok/connect`}
            data-testid="mkt-tiktok-connect"
            className="cg-btn-primary mt-3 inline-flex !min-h-[36px] items-center gap-1.5 text-[13px]"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Connect TikTok
          </a>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {redirectUri ? (
            <div className="rounded-lg p-3" style={{ background: "hsla(42,55%,94%,0.85)", border: "1px solid hsla(38,40%,75%,0.7)" }}>
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">your callback URL</p>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-white/60 px-2 py-1 font-mono text-[11px]" data-testid="mkt-tiktok-redirect-uri">
                  {redirectUri}
                </code>
                <button
                  type="button"
                  onClick={copyUri}
                  data-testid="mkt-tiktok-copy-uri"
                  className="cg-btn-ghost !min-h-[30px] !px-2 text-[11px]"
                  title="Copy callback URL"
                >
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Paste this verbatim into TikTok's "Redirect URI" field. Must be HTTPS.
              </p>
            </div>
          ) : null}
          <ol className="list-decimal space-y-2 pl-5 text-[12.5px] text-foreground/85">
            <li>
              <a href="https://developers.tiktok.com/manage/apps" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground inline-flex items-center gap-1">
                Register a TikTok app <ExternalLink className="h-3 w-3" />
              </a>{" "}
              and enable Login Kit + Content Posting API (Direct Post).
            </li>
            <li>Add the callback URL above to your TikTok app's "Redirect URI" list.</li>
            <li>Submit for audit — TikTok approves <code>video.publish</code> in 2–6 weeks.</li>
            <li>Once approved, add <code>TIKTOK_CLIENT_KEY</code> + <code>TIKTOK_CLIENT_SECRET</code> + <code>TIKTOK_REDIRECT_URI</code> to <code>backend/.env</code> and restart backend.</li>
            <li>Reload this page and click <strong>Connect TikTok</strong> to log in as the brand account.</li>
          </ol>
        </div>
      )}
    </div>
  );
}

// ── Draft card ───────────────────────────────────────────────────────
function DraftCard({ draft, token, status, onChange }) {
  const [busy, setBusy] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [edited, setEdited] = useState(null);
  const editing = !!edited;
  const view = edited || draft;
  const hasVideo = !!draft.video_path;
  const videoUrl = hasVideo
    ? `${API}/admin/marketing/queue/${draft.id}/video?t=${encodeURIComponent(token)}&v=${encodeURIComponent(draft.video_rendered_at || "")}`
    : null;

  const copyAll = () => {
    const hashtags = Array.isArray(view.hashtags) ? view.hashtags.map(h => `#${h}`).join(" ") : "";
    const onScreen = Array.isArray(view.on_screen_text) ? view.on_screen_text.join(" / ") : (view.on_screen_text || "");
    const txt = [
      `THEME: ${view.theme_slug}`,
      ``,
      `HOOK: ${view.hook}`,
      ``,
      `VOICEOVER:`,
      view.voiceover,
      ``,
      `CAPTION: ${view.caption}`,
      ``,
      `HASHTAGS: ${hashtags}`,
      ``,
      `ON-SCREEN: ${onScreen}`,
      `MUSIC VIBE: ${view.music_vibe}`,
    ].join("\n");
    navigator.clipboard.writeText(txt);
  };

  const update = async (patch) => {
    setBusy(true);
    try {
      const res = await apiFetch(token, `/admin/marketing/queue/${draft.id}`, { method: "PUT", body: JSON.stringify(patch) });
      if (res.ok) onChange();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft?")) return;
    setBusy(true);
    try {
      const res = await apiFetch(token, `/admin/marketing/queue/${draft.id}`, { method: "DELETE" });
      if (res.ok) onChange();
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!edited) return;
    await update({
      hook: edited.hook,
      voiceover: edited.voiceover,
      caption: edited.caption,
      hashtags: edited.hashtags,
    });
    setEdited(null);
  };

  const renderVideo = async (duration) => {
    const ok = window.confirm(
      `Render a ${duration}s 9:16 Sora 2 Pro video for this draft?\n\n` +
      `Cost: ~$${(duration / 4 * 1.0).toFixed(2)} via Emergent LLM key.\n` +
      `Time: 3–7 minutes (window stays open — refresh anytime).`,
    );
    if (!ok) return;
    setRendering(true);
    try {
      const res = await apiFetch(token, `/admin/marketing/queue/${draft.id}/render`, {
        method: "POST",
        body: JSON.stringify({ duration, model: "sora-2-pro" }),
      });
      if (res.status === 503) { alert("EMERGENT_LLM_KEY not set in backend/.env."); return; }
      if (!res.ok) {
        const err = await res.json();
        alert(`Render failed: ${JSON.stringify(err.detail || err)}`);
        return;
      }
      onChange();
    } finally { setRendering(false); }
  };

  const publish = async () => {
    if (!hasVideo) { alert("Render a video first."); return; }
    if (!status?.connected) { alert("Connect TikTok first (right panel)."); return; }
    if (!window.confirm("Publish this draft to TikTok? This will post immediately under your brand account.")) return;
    setBusy(true);
    try {
      const res = await apiFetch(token, `/admin/marketing/queue/${draft.id}/publish`, {
        method: "POST",
        body: JSON.stringify({ video_path: draft.video_path }),
      });
      if (res.status === 503) { alert("TikTok not configured."); return; }
      if (!res.ok) {
        const err = await res.json();
        alert(`Publish failed: ${JSON.stringify(err.detail || err)}`);
        return;
      }
      const data = await res.json();
      alert(`Posted to TikTok ✓\npublish_id: ${data.publish_id}`);
      onChange();
    } finally { setBusy(false); }
  };

  const statusStyle =
    draft.status === "posted"   ? { background: "hsla(110,40%,88%,0.85)", color: "hsl(120 30% 25%)" } :
    draft.status === "approved" ? { background: "hsla(214,55%,93%,0.85)", color: "hsl(214 40% 25%)" } :
    draft.status === "rejected" ? { background: "hsla(0,40%,90%,0.85)", color: "hsl(0 30% 30%)" } :
                                  { background: "hsla(42,60%,90%,0.85)", color: "hsl(30 30% 25%)" };

  const hashtagStr = Array.isArray(view.hashtags) ? view.hashtags.join(", ") : "";

  return (
    <div className="cg-card rounded-2xl p-5" data-testid={`mkt-draft-${draft.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            <span style={statusStyle} className="rounded-full px-2 py-0.5 text-[10px] font-medium">{draft.status}</span>
            <span>{draft.theme_slug}</span>
            <span>·</span>
            <span>{new Date(draft.created_at).toLocaleString()}</span>
            {hasVideo ? <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px]" style={{ background: "hsla(214,55%,93%,0.85)", color: "hsl(214 40% 25%)" }}><Film className="h-2.5 w-2.5" /> {draft.video_duration_s || "?"}s video</span> : null}
            {draft.render_status === "rendering" ? <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px]" style={{ background: "hsla(42,60%,90%,0.85)", color: "hsl(30 30% 25%)" }}><Loader2 className="h-2.5 w-2.5 animate-spin" /> rendering…</span> : null}
            {draft.render_status === "failed" ? <span title={draft.render_error || ""} className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: "hsla(0,40%,90%,0.85)", color: "hsl(0 30% 30%)" }}>render failed</span> : null}
          </div>
          {editing ? (
            <input
              value={edited.hook} onChange={(e) => setEdited({ ...edited, hook: e.target.value })}
              className="mt-3 w-full rounded-lg border bg-white/70 px-3 py-2 text-[15px] font-medium"
              style={{ borderColor: "hsla(38,25%,70%,0.8)" }}
            />
          ) : (
            <h4 className="mt-2 text-[15px] font-medium leading-snug text-foreground">{view.hook}</h4>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <button type="button" disabled={busy} onClick={save} className="cg-btn-primary !min-h-[32px] !px-3 text-[12px]" data-testid={`mkt-save-${draft.id}`}>
                <Check className="h-3 w-3" />
              </button>
              <button type="button" disabled={busy} onClick={() => setEdited(null)} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]">
                <X className="h-3 w-3" />
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={copyAll} title="Copy all text" className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]" data-testid={`mkt-copy-${draft.id}`}>
                <Copy className="h-3 w-3" />
              </button>
              <button type="button" onClick={() => setEdited({ hook: draft.hook, voiceover: draft.voiceover, caption: draft.caption, hashtags: draft.hashtags || [] })} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]">
                Edit
              </button>
              {!hasVideo ? (
                <button
                  type="button" disabled={rendering || draft.render_status === "rendering"} onClick={() => renderVideo(8)}
                  className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]"
                  data-testid={`mkt-render-${draft.id}`}
                  title="Render a 9:16 Sora 2 video"
                >
                  {(rendering || draft.render_status === "rendering") ? <Loader2 className="h-3 w-3 animate-spin" /> : <Film className="h-3 w-3" />}
                  <span className="ml-1">{(rendering || draft.render_status === "rendering") ? "Rendering" : "Render"}</span>
                </button>
              ) : (
                <button
                  type="button" disabled={rendering || draft.render_status === "rendering"} onClick={() => renderVideo(8)}
                  className="cg-btn-ghost !min-h-[32px] !px-3 text-[11px]"
                  data-testid={`mkt-rerender-${draft.id}`}
                  title="Re-render video"
                >
                  {(rendering || draft.render_status === "rendering") ? <Loader2 className="h-3 w-3 animate-spin" /> : <Film className="h-3 w-3" />}
                  <span className="ml-1">{(rendering || draft.render_status === "rendering") ? "Rendering" : "Re-render"}</span>
                </button>
              )}
              {hasVideo && status?.connected && draft.status !== "posted" ? (
                <button
                  type="button" disabled={busy} onClick={publish}
                  className="cg-btn-primary !min-h-[32px] !px-3 text-[12px]"
                  data-testid={`mkt-publish-${draft.id}`}
                >
                  <Send className="h-3 w-3" /><span className="ml-1">Publish</span>
                </button>
              ) : null}
              {draft.status !== "approved" && draft.status !== "posted" ? (
                <button type="button" disabled={busy} onClick={() => update({ status: "approved" })} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]" data-testid={`mkt-approve-${draft.id}`}>
                  Approve
                </button>
              ) : null}
              {draft.status === "approved" ? (
                <button type="button" disabled={busy} onClick={() => update({ status: "posted" })} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]" data-testid={`mkt-mark-posted-${draft.id}`}>
                  Mark posted
                </button>
              ) : null}
              <button type="button" disabled={busy} onClick={remove} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]" data-testid={`mkt-delete-${draft.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      </div>

      {hasVideo ? (
        <div className="mt-4 max-w-[220px]">
          <video
            src={videoUrl}
            controls
            data-testid={`mkt-video-${draft.id}`}
            className="aspect-[9/16] w-full rounded-lg bg-black"
          />
        </div>
      ) : null}

      <div className="mt-3 space-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Voiceover (~25s)</p>
          {editing ? (
            <textarea
              value={edited.voiceover} onChange={(e) => setEdited({ ...edited, voiceover: e.target.value })}
              rows={6}
              className="mt-1 w-full rounded-lg border bg-white/70 px-3 py-2 text-[13px] leading-relaxed"
              style={{ borderColor: "hsla(38,25%,70%,0.8)" }}
            />
          ) : (
            <p className="mt-1 whitespace-pre-wrap rounded-lg bg-white/40 px-3 py-2 text-[13px] leading-relaxed text-foreground/85">{view.voiceover}</p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Caption</p>
          {editing ? (
            <textarea
              value={edited.caption} onChange={(e) => setEdited({ ...edited, caption: e.target.value })}
              rows={2}
              className="mt-1 w-full rounded-lg border bg-white/70 px-3 py-2 text-[13px]"
              style={{ borderColor: "hsla(38,25%,70%,0.8)" }}
            />
          ) : (
            <p className="mt-1 rounded-lg bg-white/40 px-3 py-2 text-[13px] text-foreground/85">{view.caption}</p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Hashtags</p>
            {editing ? (
              <input
                value={Array.isArray(edited.hashtags) ? edited.hashtags.join(", ") : ""}
                onChange={(e) => setEdited({ ...edited, hashtags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })}
                className="mt-1 w-full rounded-lg border bg-white/70 px-3 py-2 text-[12px]"
                style={{ borderColor: "hsla(38,25%,70%,0.8)" }}
              />
            ) : (
              <p className="mt-1 rounded-lg bg-white/40 px-3 py-2 text-[12px] text-muted-foreground">{hashtagStr ? `#${view.hashtags.join(" #")}` : "—"}</p>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Music vibe / on-screen</p>
            <p className="mt-1 rounded-lg bg-white/40 px-3 py-2 text-[12px] text-muted-foreground">
              {view.music_vibe || "—"}
              {Array.isArray(view.on_screen_text) && view.on_screen_text.length > 0
                ? ` · ${view.on_screen_text.join(" / ")}`
                : view.on_screen_text ? ` · ${view.on_screen_text}` : ""}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────
export default function Marketing() {
  const [token, setTok] = useState(() => getToken());
  const [queue, setQueue] = useState(null);
  const [status, setStatus] = useState(null);
  const [themes, setThemes] = useState([]);
  const [today, setToday] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [theme, setTheme] = useState("");

  const refresh = useMemo(() => async () => {
    if (!token) return;
    const [qRes, sRes, tRes] = await Promise.all([
      apiFetch(token, `/admin/marketing/queue${filter === "all" ? "" : `?status=${filter}`}`),
      apiFetch(token, "/admin/marketing/tiktok/status"),
      apiFetch(token, "/admin/marketing/themes"),
    ]);
    if (qRes.ok) setQueue(await qRes.json());
    if (sRes.ok) setStatus(await sRes.json());
    if (tRes.ok) {
      const t = await tRes.json();
      setThemes(t.themes || []);
      setToday(t.today);
    }
  }, [token, filter]);

  useEffect(() => { refresh(); }, [refresh]);

  // While any draft is rendering, auto-refresh every 12s so the video pops
  // in the moment Sora 2 finishes (no manual reload needed).
  useEffect(() => {
    if (!queue) return;
    const anyRendering = queue.drafts.some(d => d.render_status === "rendering");
    if (!anyRendering) return undefined;
    const id = setInterval(refresh, 12000);
    return () => clearInterval(id);
  }, [queue, refresh]);

  // Surface TikTok connect-callback toasts from the URL
  const [toast, setToast] = useState("");
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const c = params.get("connect");
      if (c === "ok") setToast("TikTok connected ✓");
      else if (c === "err") setToast(`TikTok connect failed: ${params.get("reason") || "unknown"}`);
      if (c) {
        const url = window.location.pathname;
        window.history.replaceState({}, "", url);
        setTimeout(() => setToast(""), 6000);
      }
    } catch { /* ignore */ }
  }, []);

  if (!token) return <TokenGate onAuthed={setTok} />;

  const generate = async () => {
    setBusy(true);
    try {
      const t = theme || today?.slug || "";
      const url = `/admin/marketing/draft/generate${t ? `?theme_slug=${encodeURIComponent(t)}` : ""}`;
      const res = await apiFetch(token, url, { method: "POST" });
      if (res.status === 503) { alert("EMERGENT_LLM_KEY not set in backend/.env."); return; }
      if (!res.ok) { const err = await res.json(); alert(`Generation failed: ${JSON.stringify(err.detail || err)}`); return; }
      await refresh();
    } finally { setBusy(false); }
  };

  return (
    <main className="cg-page-bg min-h-screen px-6 pb-24 pt-8" data-testid="mkt-dashboard">
      <Link to="/admin" className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Admin
      </Link>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif-display text-3xl text-foreground">Marketing Command Center</h1>
          <p className="text-[13px] text-muted-foreground">
            Daily auto-drafts · TikTok autonomous posting · Brand outreach
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {today ? (
            <span className="rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground" style={{ background: "hsla(42,55%,94%,0.85)", border: "1px solid hsla(38,40%,75%,0.7)" }}>
              today's theme · {today.slug}
            </span>
          ) : null}
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            data-testid="mkt-theme-select"
            className="rounded-lg border bg-white/70 px-2.5 py-1.5 text-[12px]"
            style={{ borderColor: "hsla(38,25%,70%,0.8)" }}
          >
            <option value="">Theme: today's</option>
            {themes.map(t => <option key={t.slug} value={t.slug}>{t.slug}</option>)}
          </select>
          <button
            type="button"
            data-testid="mkt-generate-btn"
            disabled={busy}
            onClick={generate}
            className="cg-btn-primary !min-h-[36px] text-[13px]"
          >
            <span className="inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Generate draft
            </span>
          </button>
        </div>
      </div>

      {toast ? (
        <div data-testid="mkt-toast" className="cg-card mt-4 rounded-2xl px-4 py-2 text-[13px]">
          {toast}
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Drafts</p>
            <span className="text-[11px] text-muted-foreground">
              ({queue?.counts ? Object.entries(queue.counts).map(([k, n]) => `${k}: ${n}`).join(" · ") : "0"})
            </span>
            <div className="ml-auto flex flex-wrap gap-1">
              {["all", "draft", "approved", "posted", "rejected"].map(f => (
                <button
                  key={f} type="button" onClick={() => setFilter(f)}
                  data-testid={`mkt-filter-${f}`}
                  className="rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.14em]"
                  style={filter === f
                    ? { background: "hsl(36 10% 9%)", color: "hsl(43 45% 96%)" }
                    : { background: "hsla(0,0%,100%,0.55)", border: "1px solid hsla(38,25%,82%,0.7)", color: "hsl(30 9% 45%)" }
                  }
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {!queue ? (
            <Loader2 className="mx-auto mt-6 h-6 w-6 animate-spin text-muted-foreground" />
          ) : queue.drafts.length === 0 ? (
            <div className="cg-card rounded-2xl p-6 text-center text-[13px] text-muted-foreground">
              No drafts yet. Click "Generate draft" above. Otherwise, the daily auto-generator will create one at 13:00 UTC (~8 AM Central).
            </div>
          ) : (
            queue.drafts.map(d => <DraftCard key={d.id} draft={d} token={token} status={status} onChange={refresh} />)
          )}
        </div>

        <div className="space-y-4">
          <TikTokPanel token={token} status={status} onRefresh={refresh} />
          <div className="cg-card rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">How this works</p>
            <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-[12.5px] text-foreground/85">
              <li>Claude Sonnet 4.5 auto-writes one draft per day at 13:00 UTC.</li>
              <li>Theme rotates through 10 spiritual / emotional angles.</li>
              <li>You edit, approve, or generate more — then copy/paste to TikTok manually <em>until</em> TikTok approves your Direct Post audit.</li>
              <li>Once TikTok keys are set & the brand is connected, the daily pipeline can post unattended.</li>
            </ol>
          </div>
        </div>
      </div>
    </main>
  );
}
